import type * as React from 'react';
import { createPortal } from 'react-dom';
import { Drawer, Paper, Transition, Box } from '@mantine/core';

export interface KianFlyoutPanelProps {
  open: boolean;
  /** Mobile: Vollbild-Drawer statt Portal */
  useDrawer: boolean;
  onDrawerClose: () => void;
  /** Desktop docked: right-edge overlay flyout */
  isDocked?: boolean;
  /** Floating / Fullscreen Desktop */
  paperStyle: React.CSSProperties;
  transitionStyles: React.CSSProperties;
  children: React.ReactNode;
  /** Nur Floating Desktop: Resize-Griff oben links */
  showResizeHandle?: boolean;
  resizePointerHandlers?: {
    onPointerDown?: (e: React.PointerEvent<Element>) => void;
    onPointerMove?: (e: React.PointerEvent<Element>) => void;
    onPointerUp?: (e: React.PointerEvent<Element>) => void;
  };
}

export function KianFlyoutPanel({
  open,
  useDrawer,
  onDrawerClose,
  isDocked,
  paperStyle,
  transitionStyles,
  children,
  showResizeHandle,
  resizePointerHandlers,
}: KianFlyoutPanelProps) {
  if (useDrawer) {
    return (
      <Drawer
        opened={open}
        onClose={onDrawerClose}
        position="right"
        size="100%"
        zIndex={5200}
        padding={0}
        withCloseButton={false}
        styles={{
          inner: { overflow: 'hidden' },
          content: { height: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
          body: {
            padding: 0,
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          },
        }}
        aria-label="KIAN Assistent"
      >
        {children}
      </Drawer>
    );
  }

  const dockedStyle: React.CSSProperties = isDocked
    ? {
        position: 'fixed',
        top: 56,
        right: 0,
        bottom: 0,
        height: 'auto',
        maxWidth: '100vw',
        borderRadius: 0,
        borderTop: 'none',
        borderRight: 'none',
        borderBottom: 'none',
      }
    : {};

  return createPortal(
    <Transition mounted={open} transition="slide-left" duration={220}>
      {(trStyles) => (
        <Paper
          shadow="xl"
          withBorder
          p={0}
          style={{
            ...trStyles,
            ...transitionStyles,
            position: 'fixed',
            zIndex: 5200,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: 'rgba(33, 33, 33, 0.82)',
            backdropFilter: 'blur(16px) saturate(160%)',
            WebkitBackdropFilter: 'blur(16px) saturate(160%)',
            borderColor: 'rgba(255, 255, 255, 0.06)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(255, 102, 0, 0.04)',
            ...paperStyle,
            ...dockedStyle,
          }}
          role="dialog"
          aria-modal="false"
          aria-label="KIAN Assistent"
        >
          {showResizeHandle && resizePointerHandlers ? (
            <Box
              onPointerDown={(e) => {
                e.stopPropagation();
                resizePointerHandlers.onPointerDown?.(e);
              }}
              onPointerMove={(e) => resizePointerHandlers.onPointerMove?.(e)}
              onPointerUp={(e) => resizePointerHandlers.onPointerUp?.(e)}
              style={
                isDocked
                  ? {
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: 6,
                      height: '100%',
                      cursor: 'col-resize',
                      zIndex: 10,
                    }
                  : {
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: 14,
                      height: 14,
                      cursor: 'nwse-resize',
                      zIndex: 10,
                    }
              }
              aria-hidden
            />
          ) : null}
          {children}
        </Paper>
      )}
    </Transition>,
    document.body
  );
}
